import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import { useQuery } from 'react-query';
import api, { API_ENDPOINTS } from '../config/api';
import axios from 'axios';
import { Contract, formatUnits } from 'ethers';
import { USDC_TOKEN_ABI, USDC_TOKEN_ADDRESS } from '../config/Constants';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Star,
  Clock,
  CheckCircle,
  Archive,
  Eye,
  Plus,
  ArrowUpRight,
  MessageCircle,
  Bell,
  Award,
  Target
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import MyGigsPage from './MyGigsPage';
import MyFreelanceJobsPage from './MyFreelanceJobsPage';
import MyFullTimeJobsPage from './MyFullTimeJobsPage';
import MyJobsPage from './MyJobsPage';
import MyOrderedGigsPage from './MyOrderedGigsPage';
import MyFreelanceJobsClientPage from './MyFreelanceJobsClientPage';
import MyFullTimeJobsClientPage from './MyFullTimeJobsClientPage';

const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const { isConnected, walletAddress, provider } = useWeb3();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [usdcPrice, setUsdcPrice] = useState(null);
  const [usdcBalance, setUsdcBalance] = useState(0);

  // Fetch USDC price for USD conversion (treated as ~1:1)
  const { data: usdcPriceData } = useQuery(
    'usdcPrice',
    async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
        return response.data;
      } catch (error) {
        console.error("Failed to get USDC price from backend:", error);
        return null;
      }
    },
    {
      select: (data) => data,
      staleTime: 300000,
      refetchInterval: 300000,
      onSuccess: (data) => {
        if (data?.usd) {
          setUsdcPrice(data.usd);
        }
      }
    }
  );

  // Update usdcPrice state when data changes
  useEffect(() => {
    if (usdcPriceData?.usd) {
      setUsdcPrice(usdcPriceData.usd);
    }
  }, [usdcPriceData]);

  // Fetch USDC token balance for connected wallet
  useEffect(() => {
    const fetchUsdc = async () => {
      try {
        if (!isConnected || !provider || !walletAddress) {
          setUsdcBalance(0);
          return;
        }
        const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, provider);
        const [decimals, bal] = await Promise.all([
          token.decimals(),
          token.balanceOf(walletAddress)
        ]);
        setUsdcBalance(parseFloat(formatUnits(bal, Number(decimals))) || 0);
      } catch (e) {
        console.error('Failed to fetch USDC balance:', e);
        setUsdcBalance(0);
      }
    };
    fetchUsdc();
  }, [isConnected, provider, walletAddress]);

  // Calculate available balance in USD from connected wallet
  const availableBalanceUSD = useMemo(() => {
    if (!isConnected || !usdcPrice) return 0;
    return usdcBalance * Number(usdcPrice);
  }, [isConnected, usdcBalance, usdcPrice]);

  // Fetch dashboard stats
  const { data: dashboardStats, isLoading } = useQuery(
    'dashboardStats',
    () => api.get(API_ENDPOINTS.USERS.DASHBOARD_STATS),
    {
      select: (data) => data.data,
    }
  );

  useEffect(() => {
    if (!dashboardStats?.user || !user) return;

    const mergedUser = {
      ...user,
      stats: dashboardStats.user.stats ?? user.stats,
      wallet: dashboardStats.user.wallet ?? user.wallet,
      referral: dashboardStats.user.referral ?? user.referral
    };

    // Only update if user data actually changed to prevent infinite loops
    const userChanged = 
      JSON.stringify(mergedUser.stats) !== JSON.stringify(user.stats) ||
      JSON.stringify(mergedUser.wallet) !== JSON.stringify(user.wallet) ||
      JSON.stringify(mergedUser.referral) !== JSON.stringify(user.referral);

    if (userChanged) {
      updateUser(mergedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardStats?.user]);

  const stats = dashboardStats?.user?.stats || user?.stats || {};
  const wallet = dashboardStats?.user?.wallet || user?.wallet || {};
  const activity = dashboardStats?.activity || {};
  const metrics = dashboardStats?.metrics || {};
  const ratingMetrics = metrics.rating || stats.rating || { average: 0, count: 0 };
  const activityPoints = metrics.activityPoints ?? stats.activityPoints ?? 0;
  const monthlyEarnings = metrics.monthlyEarnings || 0;
  const monthlySpending = metrics.monthlySpending || 0;
  const skillsAssessment = metrics.skillsAssessment || 'Developing';
  const referralLobTokens = metrics.referralLobTokens || dashboardStats?.user?.referral?.lobTokens || {
    pending: 0,
    available: 0,
    withdrawn: 0
  };
  const jobStatusSummary = activity.jobStatusSummary || {};
  const gigStatusSummary = activity.gigStatusSummary || {};
  const skillsCount = Array.isArray(user?.profile?.skills) ? user.profile.skills.length : 0;
  const completedProjects = stats.jobsCompleted || 0;

  const jobStatusCards = [
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Open Jobs',
      value: jobStatusSummary.open || 0,
      subtitle: 'Awaiting kick-off',
      color: 'indigo'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Jobs In Progress',
      value: jobStatusSummary['in-progress'] || 0,
      subtitle: 'Currently active',
      color: 'orange'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Jobs Completed',
      value: jobStatusSummary.completed || 0,
      subtitle: 'Successfully delivered',
      color: 'green'
    },
    {
      icon: <Archive className="w-8 h-8" />,
      title: 'Jobs Cancelled',
      value: jobStatusSummary.cancelled || 0,
      subtitle: 'Closed without delivery',
      color: 'gray'
    }
  ];

  const gigStatusCards = user?.role === 'talent'
    ? [
        {
          icon: <Eye className="w-8 h-8" />,
          title: 'Active Gigs',
          value: gigStatusSummary.active || 0,
          subtitle: 'Visible to clients',
          color: 'purple'
        },
        {
          icon: <Clock className="w-8 h-8" />,
          title: 'Gigs In Progress',
          value: gigStatusSummary['in-progress'] || 0,
          subtitle: 'Deliveries underway',
          color: 'orange'
        },
        {
          icon: <CheckCircle className="w-8 h-8" />,
          title: 'Gigs Completed',
          value: gigStatusSummary.completed || 0,
          subtitle: 'Work delivered',
          color: 'green'
        },
        {
          icon: <Archive className="w-8 h-8" />,
          title: 'Gigs Paused',
          value: gigStatusSummary.paused || gigStatusSummary.cancelled || 0,
          subtitle: 'Temporarily hidden',
          color: 'gray'
        }
      ]
    : [
        {
          icon: <Eye className="w-8 h-8" />,
          title: 'Pending Orders',
          value: gigStatusSummary.pending || 0,
          subtitle: 'Awaiting approval',
          color: 'yellow'
        },
        {
          icon: <Clock className="w-8 h-8" />,
          title: 'Accepted Orders',
          value: gigStatusSummary.accepted || 0,
          subtitle: 'Matched with talent',
          color: 'blue'
        },
        {
          icon: <Clock className="w-8 h-8" />,
          title: 'Orders In Progress',
          value: gigStatusSummary['in-progress'] || 0,
          subtitle: 'Being delivered',
          color: 'orange'
        },
        {
          icon: <CheckCircle className="w-8 h-8" />,
          title: 'Orders Completed',
          value: gigStatusSummary.completed || 0,
          subtitle: 'Delivered to you',
          color: 'green'
        }
      ];

  // Handle tab routing
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Render specific tab content
  if (activeTab === 'gigs') {
    if (user?.role === 'talent') {
      return <MyGigsPage />;
    } else {
      return <MyOrderedGigsPage />;
    }
  }
  
  if (activeTab === 'freelance-jobs') {
    if (user?.role === 'talent') {
      return <MyFreelanceJobsPage />;
    } else {
      return <MyFreelanceJobsClientPage />;
    }
  }
  
  if (activeTab === 'full-time-jobs') {
    if (user?.role === 'talent') {
      return <MyFullTimeJobsPage />;
    } else {
      return <MyFullTimeJobsClientPage />;
    }
  }
  
  if (activeTab === 'others') {
    // For "others", show jobs/gigs that are not freelance or full-time
    // This includes part-time, contract jobs, and labour gigs
    return <MyJobsPage />;
  }

  const StatCard = ({ icon, title, value, subtitle, color = 'accent', trend }) => (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className={`text-2xl font-bold text-${color}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`text-${color}`}>
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center space-x-1">
          <ArrowUpRight className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-600 dark:text-green-400">{trend}</span>
        </div>
      )}
    </div>
  );

  const QuickActionCard = ({ icon, title, description, onClick, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="card p-6 text-left hover:shadow-lg transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex items-center space-x-3">
        <div className="text-accent">{icon}</div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
        </div>
      </div>
    </button>
  );

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Welcome back, {user?.profile?.firstName || user?.username}! 👋
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Here's what's happening with your account today.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<DollarSign className="w-8 h-8" />}
          title="Available Balance"
          value={`${usdcBalance.toFixed(2)} USDC`}
          subtitle={
            isConnected
              ? usdcPrice
                ? `~$${availableBalanceUSD.toFixed(2)} USD (wallet USDC)`
                : 'Wallet USDC (USD price loading...)'
              : 'Connect wallet to view balance'
          }
          color="green"
          trend=""
        />
        
        <StatCard
          icon={<Star className="w-8 h-8" />}
          title="Rating"
          value={ratingMetrics.average ? ratingMetrics.average.toFixed(1) : '0.0'}
          subtitle={`${ratingMetrics.count || 0} reviews`}
          trend=""
        />
        
        <StatCard
          icon={<TrendingUp className="w-8 h-8" />}
          title="Activity Points"
          value={activityPoints}
          subtitle={`${activity.activeJobs || 0} active projects`}
          color="blue"
          trend=""
        />
        
        <StatCard
          icon={user?.role === 'talent' ? <TrendingUp className="w-8 h-8" /> : <TrendingDown className="w-8 h-8" />}
          title={user?.role === 'talent' ? 'Monthly Earnings' : 'Monthly Spend'}
          value={`$${(user?.role === 'talent' ? monthlyEarnings : monthlySpending).toFixed(2)}`}
          subtitle="Last 30 days on platform"
          color={user?.role === 'talent' ? 'blue' : 'red'}
        />
      </div>

      {/* Referral LOB Tokens Stats */}
      {(referralLobTokens.pending > 0 || referralLobTokens.available > 0 || referralLobTokens.withdrawn > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={<Clock className="w-8 h-8" />}
            title="Pending LOB Tokens"
            value={referralLobTokens.pending || 0}
            subtitle="Awaiting job completion"
            color="yellow"
          />
          
          <StatCard
            icon={<Award className="w-8 h-8" />}
            title="Available LOB Tokens"
            value={referralLobTokens.available || 0}
            subtitle="Ready to withdraw"
            color="green"
          />
          
          <StatCard
            icon={<Target className="w-8 h-8" />}
            title="Withdrawn LOB Tokens"
            value={referralLobTokens.withdrawn || 0}
            subtitle="Already withdrawn"
            color="blue"
          />
        </div>
      )}

      {/* Work Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {jobStatusCards.map((card) => (
          <StatCard
            key={card.title}
            icon={card.icon}
            title={card.title}
            value={card.value}
            subtitle={card.subtitle}
            color={card.color}
          />
        ))}
      </div>

      {/* Gig Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {gigStatusCards.map((card) => (
          <StatCard
            key={card.title}
            icon={card.icon}
            title={card.title}
            value={card.value}
            subtitle={card.subtitle}
            color={card.color}
          />
        ))}
      </div>

      {user?.role === 'talent' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={<Award className="w-8 h-8" />}
            title="Skills Assessment"
            value={skillsAssessment}
            subtitle={`${skillsCount} skills • ${completedProjects} completed jobs`}
            color="purple"
          />
        </div>
      )}

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-heading font-bold text-heading mb-6">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user?.role === 'talent' ? (
              <>
                <QuickActionCard
                  icon={<Plus className="w-6 h-6" />}
                  title="Create New Gig"
                  description="Post a new service offering"
                  onClick={() => window.location.href = '/create-gig'}
                />
                <QuickActionCard
                  icon={<Eye className="w-6 h-6" />}
                  title="Browse Jobs"
                  description="Find freelance opportunities"
                  onClick={() => window.location.href = '/jobs'}
                />
                <QuickActionCard
                  icon={<MessageCircle className="w-6 h-6" />}
                  title="Check Messages"
                  description="View your conversations"
                  onClick={() => window.location.name = '/chats'}
                />
                <QuickActionCard
                  icon={<Target className="w-6 h-6" />}
                  title="Set Goals"
                  description="Track your progress"
                  onClick={() => setActiveTab('goals')}
                />
              </>
            ) : (
              <>
                <QuickActionCard
                  icon={<Plus className="w-6 h-6" />}
                  title="Post New Job"
                  description="Create a new project listing"
                  onClick={() => window.location.href = '/create-job'}
                />
                <QuickActionCard
                  icon={<Eye className="w-6 h-6" />}
                  title="Browse Gigs"
                  description="Find talented service providers"
                  onClick={() => window.location.href = '/gigs'}
                />
                <QuickActionCard
                  icon={<MessageCircle className="w-6 h-6" />}
                  title="Check Messages"
                  description="View your conversations"
                  onClick={() => window.location.href = '/chats'}
                />
                <QuickActionCard
                  icon={<Award className="w-6 h-6" />}
                  title="Team Management"
                  description="Manage your active talent"
                  onClick={() => setActiveTab('teams')}
                />
              </>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="text-xl font-heading font-bold text-heading mb-6">Recent Activity</h2>
          
          <div className="space-y-4">
            <div className="card p-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Project completed
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    2 hours ago
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                  <Bell className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    New message received
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    4 hours ago
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Payment received
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Yesterday
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/40 rounded-full flex items-center justify-center">
                  <Star className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    New rating received
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    2 days ago
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button className="w-full text-center text-sm text-accent hover:text-accent/80">
              View all activity
            </button>
          </div>
        </div>
      </div>

     
    </div>
  );
};

export default DashboardPage;
